import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EntityCrudState } from '@homeleite/common-components';
import { User, ControllerUser, ProducerUser, AdminUser } from '@homeleite/domain';

export interface UserDialogData {
    entity: User;
    state: EntityCrudState;
    /** Quando true (criar pelo produtor), login fica fixo no código APC. */
    lockLogin?: boolean;
    contextName?: string;
}

@Component({
    selector: 'app-user-form-dialog',
    templateUrl: './users-form-dialog.component.html',
    styleUrls: ['./users-form-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UsersFormDialogComponent implements OnInit {
    form!: FormGroup;
    state: EntityCrudState;
    entity: User;
    isEditMode: boolean;
    contextName = '';

    constructor(
        private fb: FormBuilder,
        public dialogRef: MatDialogRef<UsersFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: UserDialogData
    ) {}

    ngOnInit(): void {
        this.entity = this.data.entity;
        this.state = this.data.state;
        this.isEditMode = this.state === EntityCrudState.EDIT;
        this.contextName = this.data.contextName || '';

        this.form = this.fb.group({
            firstName: [this.entity.firstName, [Validators.required]],
            lastName: [this.entity.lastName],
            login: [
                { value: this.entity.login, disabled: !!this.data.lockLogin },
                [Validators.required]
            ],
            email: [this.entity.email, [Validators.required]],
            password: [
                '',
                this.isEditMode
                    ? [Validators.minLength(5), Validators.maxLength(30)]
                    : [Validators.required, Validators.minLength(5), Validators.maxLength(30)]
            ]
        });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.form.invalid) {
            return;
        }

        const formValue = this.form.getRawValue();

        if (this.isEditMode && !formValue.password) {
            formValue.password = null;
        }

        const entityWithPassword = { ...this.entity, ...formValue };

        let finalEntity: User;

        if (this.entity instanceof ControllerUser) {
            finalEntity = new ControllerUser(
                entityWithPassword.id,
                entityWithPassword.login,
                entityWithPassword.firstName,
                entityWithPassword.lastName,
                entityWithPassword.email,
                entityWithPassword.password,
                this.entity.controller
            );
        } else if (this.entity instanceof ProducerUser) {
            finalEntity = new ProducerUser(
                entityWithPassword.id,
                entityWithPassword.login,
                entityWithPassword.firstName,
                entityWithPassword.lastName,
                entityWithPassword.email,
                entityWithPassword.password,
                this.entity.producer
            );
        } else if (this.entity instanceof AdminUser) {
            finalEntity = new AdminUser(
                this.entity.id,
                formValue.login,
                formValue.firstName,
                formValue.lastName,
                formValue.email
            );
        }

        this.dialogRef.close({
            entity: finalEntity
        });
    }
}
